<script setup lang="ts">
/**
 * Collapsible section inside a settings form — groups fields that are real
 * but secondary (hidden fields, submit/error copy, technical toggles) behind
 * a closed-by-default disclosure, so the form's first screen is the handful
 * of fields a tenant actually needs to fill in rather than every field the
 * block supports.
 *
 * `defaultOpen` lets a form start a group open when it already holds data
 * worth seeing (e.g. hidden fields on a duplicated block) rather than hiding
 * something the tenant configured.
 */
const props = withDefaults(defineProps<{
  label: string
  defaultOpen?: boolean
}>(), { defaultOpen: false })

const open = ref(props.defaultOpen)
</script>

<template>
  <div class="rounded-lg border border-default">
    <button
      type="button"
      class="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-muted"
      @click="open = !open"
    >
      {{ label }}
      <UIcon
        :name="open ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
        class="size-3.5 shrink-0"
      />
    </button>
    <div
      v-if="open"
      class="space-y-3 border-t border-default p-3"
    >
      <slot />
    </div>
  </div>
</template>
